import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@mui/material';
import { Stack } from '@mui/system';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import CreateShippingProvider from '@/shared/types/shipping/createShippingProvider.type';
import ShippingProvider from '@/shared/types/shipping/shippingProvider.type';
import ShippingProviderKey from '@/shared/types/shipping/shippingProviderKey.type';

type ActiveView = 'form' | 'error';

type ShippingProviderForm = Record<keyof CreateShippingProvider, string>;

type ManageShippingProviderDialogProps = {
  shippingProviderKeys: ShippingProviderKey[];
  shippingProvider?: ShippingProvider | null;
  errors?: string[];
  onClose: () => void;
  proceed: (
    provider: CreateShippingProvider,
    action: 'add' | 'update',
    providerId?: string,
  ) => void;
};

const ManageShippingProviderDialog: FC<ManageShippingProviderDialogProps> = ({
  shippingProviderKeys,
  shippingProvider,
  errors,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['shipping', 'forms', 'common']);

  const [view, setView] = useState<ActiveView>('form');
  const [form, setForm] = useState<ShippingProviderForm>({
    shipping_provider_key: shippingProvider?.shipping_provider_key || '',
    name: shippingProvider?.name || '',
    api_key: shippingProvider?.api_key || '',
    api_url: shippingProvider?.api_url || '',
  });
  const [formErrors, setFormErrors] = useState<Partial<ShippingProviderForm>>(
    {},
  );

  const options = shippingProviderKeys.map((key) => ({
    label: key.shipping_provider_name,
    value: key.shipping_provider_key,
  }));

  const providerId = shippingProvider?.shipping_provider_id || '';

  const onValidate = () => {
    const errors: Partial<ShippingProviderForm> = {};

    if (!form.shipping_provider_key) {
      errors.shipping_provider_key = t('forms:shipping_provider_key_required');
    }

    if (!form.name) {
      errors.name = t('forms:name_required');
    }

    if (!form.api_key) {
      errors.api_key = t('forms:api_key_required');
    }

    if (!form.api_url) {
      errors.api_url = t('forms:api_url_required');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (errors && errors.length > 0) {
      setView('error');
    }
  }, [errors]);

  return (
    <ModalNotification
      fullWidth
      size='sm'
      open
      onClose={onClose}
      dialogProps={{
        title: shippingProvider
          ? t('shipping:updateShippingProvider')
          : t('shipping:addShippingProvider'),
        content: (
          <Stack py={2}>
            {view === 'form' && (
              <WaveUIForm_V2
                fields={[
                  {
                    field_type: FieldType.Select,
                    field_props: {
                      name: 'shipping_provider_key',
                      label: t('forms:shipping_provider_key'),
                      value: form.shipping_provider_key,
                      placeholder: t('forms:shipping_provider_key'),
                      options,
                      error: !!formErrors.shipping_provider_key,
                      helperText: formErrors.shipping_provider_key,
                    },
                  },
                  {
                    field_type: FieldType.Text,
                    field_props: {
                      name: 'name',
                      label: t('forms:name'),
                      value: form.name,
                      placeholder: t('forms:name'),
                      error: !!formErrors.name,
                      helperText: formErrors.name,
                    },
                  },
                  {
                    field_type: FieldType.Text,
                    field_props: {
                      name: 'api_key',
                      label: t('forms:api_key'),
                      value: form.api_key,
                      placeholder: t('forms:api_key'),
                      error: !!formErrors.api_key,
                      helperText:
                        formErrors.api_key ||
                        t('forms:api_key_shipping_helper'),
                    },
                  },
                  {
                    field_type: FieldType.Text,
                    field_props: {
                      name: 'api_url',
                      label: t('forms:api_url'),
                      value: form.api_url,
                      placeholder: t('forms:api_url'),
                      error: !!formErrors.api_url,
                      helperText:
                        formErrors.api_url ||
                        t('forms:api_url_shipping_helper'),
                    },
                  },
                ]}
                getFieldsValues={(value) =>
                  setForm((prev) => ({ ...prev, ...value }))
                }
              />
            )}

            {view === 'error' && (
              <Stack direction='column' gap={2}>
                {errors?.map((error, index) => (
                  <Alert key={index} severity='error'>
                    {error}
                  </Alert>
                ))}
              </Stack>
            )}
          </Stack>
        ),
        actions: [
          view === 'error'
            ? {
                label: t('common:close'),
                variant: 'contained',
                color: 'primary',
                fullWidth: true,
                onClick: onClose,
              }
            : {
                label: shippingProvider ? t('common:update') : t('common:add'),
                variant: 'contained',
                color: 'primary',
                fullWidth: true,
                onClick: () => {
                  const isValid = onValidate();

                  if (isValid) {
                    proceed(
                      form,
                      shippingProvider ? 'update' : 'add',
                      providerId,
                    );
                  }
                },
              },
        ],
      }}
    />
  );
};

export default ManageShippingProviderDialog;
